<script lang="ts">
  import { getRules, updateRule, deleteRule, applyRuleToExisting, getCategories, getPeople, createRule } from '../lib/storage.js';
  import * as categoryOperations from '../lib/storage/operations/categoryOperations';
  import Notification from './common/Notification.svelte';
  import ConfirmDialog from './common/ConfirmDialog.svelte';
  import { createConfirmDialog } from '../lib/confirmDialog.svelte';
  import RuleForm from './rules/RuleForm.svelte';
  import RulesTable from './rules/RulesTable.svelte';
  import type { Rule, NewRuleFormData } from '../types/rule';
  import { markChecklistItem } from '../lib/onboardingState';

  const confirmDialog = createConfirmDialog();

  let { onUpdate = () => {} }: { onUpdate: () => void } = $props();

  // State
  let rules = $state<Rule[]>([]);
  let categories = $state<string[]>([]);
  let people = $state<string[]>([]);

  let newRuleData = $state<NewRuleFormData>({
    pattern: '',
    category: '',
    person: '',
    normalizedName: '',
    markAsRecurring: false,
    recurringType: '',
    excludeFromAnalytics: false
  });

  let editingRuleId = $state<string | null>(null);
  let editingRule = $state<Rule>({} as Rule);
  let loading = $state(false);
  let error = $state<string | null>(null);
  let success = $state<string | null>(null);
  let applyingRuleId = $state<string | null>(null);
  let showAdvanced = $state(false);

  // Load rules, categories, and people on mount
  $effect(() => {
    loadRules();
    loadCategories();
    loadPeople();
  });

  async function loadRules() {
    rules = await getRules();
  }

  async function loadCategories() {
    categories = await getCategories();
  }

  async function loadPeople() {
    people = await getPeople();
  }

  async function handleCreateCategory(categoryName: string) {
    const result = await categoryOperations.add(categoryName);
    if (result.success && result.categories) {
      categories = result.categories;
    } else {
      console.error('Failed to create category:', result.error);
      throw new Error(result.error || 'Failed to create category');
    }
  }

  async function handleAddRule() {
    if (!newRuleData.pattern.trim()) {
      error = 'Pattern cannot be empty';
      return;
    }
    if (!newRuleData.category) {
      error = 'Please select a category';
      return;
    }

    loading = true;
    error = null;
    success = null;

    const ruleData = {
      pattern: newRuleData.pattern.trim(),
      category: newRuleData.category,
      person: newRuleData.person || null,
      normalizedName: newRuleData.normalizedName.trim() || null,
      markAsRecurring: newRuleData.markAsRecurring,
      recurringType: newRuleData.recurringType || null,
      excludeFromAnalytics: newRuleData.excludeFromAnalytics
    };

    try {
      await createRule(ruleData);
      await loadRules();

      // Mark onboarding checklist item as complete
      markChecklistItem('rule');
      // Reset form
      newRuleData = {
        pattern: '',
        category: '',
        person: '',
        normalizedName: '',
        markAsRecurring: false,
        recurringType: '',
        excludeFromAnalytics: false
      };
      success = 'Rule created successfully';
      onUpdate();
    } catch (err) {
      error = err.message;
    }

    loading = false;
  }

  function startEdit(rule: Rule) {
    editingRuleId = rule.id;
    editingRule = { ...rule };
  }

  function cancelEdit() {
    editingRuleId = null;
    editingRule = {} as Rule;
  }

  async function handleUpdateRule() {
    if (!editingRule.pattern.trim()) {
      error = 'Pattern cannot be empty';
      return;
    }

    loading = true;
    error = null;
    success = null;

    const updates = {
      pattern: editingRule.pattern,
      category: editingRule.category,
      person: editingRule.person,
      enabled: editingRule.enabled,
      normalizedName: editingRule.normalizedName || null,
      markAsRecurring: editingRule.markAsRecurring || false,
      recurringType: editingRule.recurringType || null,
      excludeFromAnalytics: editingRule.excludeFromAnalytics || false
    };

    const result = await updateRule(editingRuleId!, updates);

    if (result.success) {
      await loadRules();
      success = 'Rule updated successfully';
      cancelEdit();
      onUpdate();
    } else {
      error = result.error;
    }

    loading = false;
  }

  async function handleDeleteRule(ruleId: string, pattern: string) {
    const confirmed = await confirmDialog.confirm({
      title: 'Delete Rule',
      message: `Are you sure you want to delete the rule for "${pattern}"?`,
      confirmText: 'Delete',
      variant: 'danger'
    });

    if (!confirmed) return;

    loading = true;
    error = null;
    success = null;

    const result = await deleteRule(ruleId);

    if (result.success) {
      await loadRules();
      success = 'Rule deleted';
      onUpdate();
    } else {
      error = result.error;
    }

    loading = false;
  }

  async function handleApplyToExisting(rule: Rule) {
    const confirmed = await confirmDialog.confirm({
      title: 'Apply Rule to Existing Transactions',
      message: `Apply this rule to all existing transactions?\n\nThis will update ${rule.pattern} → ${rule.category} for ALL transactions, including user-edited ones.`,
      confirmText: 'Apply to All',
      variant: 'warning'
    });

    if (!confirmed) return;

    applyingRuleId = rule.id;
    error = null;
    success = null;

    const result = await applyRuleToExisting(rule.id, true);

    if (result.success) {
      await loadRules();
      success = `Updated ${result.updatedCount} transaction(s)`;
      onUpdate();
    } else {
      error = result.error;
    }

    applyingRuleId = null;
  }

  async function handleToggleEnabled(rule: Rule) {
    loading = true;
    const result = await updateRule(rule.id, { enabled: !rule.enabled });
    if (result.success) {
      await loadRules();
    }
    loading = false;
  }

  function handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      if (editingRuleId) {
        handleUpdateRule();
      } else {
        handleAddRule();
      }
    } else if (event.key === 'Escape') {
      if (editingRuleId) {
        cancelEdit();
      } else {
        newRuleData = {
          pattern: '',
          category: '',
          person: '',
          normalizedName: '',
          markAsRecurring: false,
          recurringType: '',
          excludeFromAnalytics: false
        };
      }
    }
  }
</script>

<div class="rules-manager">
  <h3>Categorization Rules</h3>
  <p class="description">Rules automatically categorize transactions during CSV import based on description patterns.</p>

  {#if error}
    <Notification type="error" message={error} onDismiss={() => error = null} />
  {/if}

  {#if success}
    <Notification type="success" message={success} autoDismiss={true} onDismiss={() => success = null} />
  {/if}

  <RuleForm
    bind:formData={newRuleData}
    {categories}
    {people}
    {loading}
    bind:showAdvanced
    onSubmit={handleAddRule}
    onKeydown={handleKeydown}
    onCreateCategory={handleCreateCategory}
  />

  <RulesTable
    {rules}
    {categories}
    {people}
    bind:editingRuleId
    bind:editingRule
    {loading}
    {applyingRuleId}
    onEdit={startEdit}
    onSave={handleUpdateRule}
    onCancel={cancelEdit}
    onToggleEnabled={handleToggleEnabled}
    onApply={handleApplyToExisting}
    onDelete={handleDeleteRule}
    onKeydown={handleKeydown}
  />
</div>

<ConfirmDialog {...confirmDialog.props} />

<style lang="scss">
  .rules-manager {
    background: white;
    border-radius: var(--radius-lg);
    padding: var(--spacing-xl);
    box-shadow: var(--shadow-md);
    margin-bottom: 20px;

    h3 {
      font-size: var(--font-size-lg);
      margin-bottom: 8px;
      color: var(--color-text-primary);
    }

    .description {
      font-size: var(--font-size-sm);
      color: var(--color-text-tertiary);
      margin-bottom: 15px;
    }
  }
</style>
